import dayjs from "dayjs";
import Spinner from "@src/components/spinner";
import { CaretDownFilled } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { faClose } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Alert, Form, Input, Modal, Select } from "antd"
import TextArea from "antd/es/input/TextArea";
import { useEffect, useRef, useState } from "react";
import { bulkCancelScheduleMutation, newCancelScheduleDutyMutation } from "@src/util/mutation/cancelDuty.mutation";
import { useDispatch } from "react-redux";
import { setNotification } from '@src/middleware/redux/reducers/notification.reducer';
import { newBannedSiteMutation } from "@src/util/mutation/blockedSites.mutation";
import { deleteBannedEmployeeFutureDutiesMutation } from "../../../util/mutation/cancelDuty.mutation";

// Reasons that only make sense for a duty that has already been worked. The value is what gets
// stored; wfc-server's scheduleCancel.service maps it to the same label for the audit log.
const COMPLETED_CANCEL_REASONS = [
    { value: "not worked", label: "Duty was not actually worked" },
    { value: "recorded in error", label: "Recorded in error / duplicate" },
    { value: "client dispute", label: "Client dispute" },
];

const CANCEL_REASONS = [
    { value: "complain", label: "Employee / Guard Complain" },
    { value: "double booking", label: "Client Double Booking" },
    { value: "reschedule", label: "Reschedule" },
    { value: "other", label: "Other" },
];

// Same minimum wfc-server enforces for a completed duty's written reason.
const COMPLETED_CANCEL_MIN_COMMENT = 10;
const CONFIRM_WORD = "CONFIRM";

// Cancels one duty (cancelledDutyData) or, from the schedule page's bulk actions, several
// (bulkDuties). If any of them is already completed, the user has to give a written reason and
// type CONFIRM before the Confirm button unlocks; the server re-checks both.
export const CancelDutyModal = ({visible, close, cancelledDutyData, bulkDuties, notice, refetch, onCancelled})=>{

    const isBulk = Array.isArray(bulkDuties);
    const duties = isBulk? bulkDuties : [cancelledDutyData];
    const {_id: scheduleId, assignedEmployee:assignedEmployeeId} = cancelledDutyData || {};

    const completedCount = duties.filter((duty)=>duty?.isCompleted).length;
    const invoicedCount = duties.filter((duty)=>duty?.isCompleted && duty?.invoiced).length;
    const hasCompleted = completedCount>0;
    const reasons = hasCompleted? [...COMPLETED_CANCEL_REASONS, ...CANCEL_REASONS] : CANCEL_REASONS;

    const {Option} = Select;

    const [reason, setReason] = useState(null);
    const [comment, setComment] = useState(null);
    const [typedConfirm, setTypedConfirm] = useState("");
    const [bannedEmployeeVisible, setBannedEmployeeVisible] = useState(false);

    const trimmedComment = (comment || "").trim();
    const commentOk = hasCompleted? trimmedComment.length>=COMPLETED_CANCEL_MIN_COMMENT : reason!=="other" || trimmedComment.length>1;
    const confirmOk = !hasCompleted || typedConfirm.trim().toUpperCase()===CONFIRM_WORD;
    const btnDisabled = !reason || !commentOk || !confirmOk || duties.length===0;

    const [newCancelSchedule, {loading: newCancelScheduleLoading}] = useMutation(newCancelScheduleDutyMutation);
    const [bulkCancelSchedule, {loading: bulkCancelScheduleLoading}] = useMutation(bulkCancelScheduleMutation);
    const loading = newCancelScheduleLoading || bulkCancelScheduleLoading;
    const dispatch = useDispatch();

    const handelSubmit = async()=>{
        try{

            if(isBulk){
                const res = await bulkCancelSchedule({
                    variables:{
                        scheduleIds: duties.map((duty)=>duty?._id),
                        reason,
                        comment: trimmedComment,
                        acknowledgeCompleted: hasCompleted,
                    }
                });
                dispatch(setNotification({
                    error: false,
                    notificationState: true,
                    message: res?.data?.bulkCancelSchedule?.message || "Duties were cancelled successfully"
                }));
                onCancelled?.();
                close();
                await refetch?.();
                return;
            }

            await newCancelSchedule({
                variables:{
                    input: {
                        reason,
                        comment: trimmedComment || " ",
                        cancelledAt: dayjs(),
                        scheduleId,
                        assignedEmployeeId,
                        acknowledgeCompleted: hasCompleted,
                    }
                }
            });
            dispatch(setNotification({
                error: false,
                notificationState: true,
                message: "Duty was cancelled successfully"
            }));
            onCancelled?.();
            if(reason=="complain"){
                setBannedEmployeeVisible(true);
            }else{
                close();
            }
            await refetch?.();
            
        }catch(err){
            dispatch(setNotification({
                error: true,
                notificationState: true,
                message: err?.message || "An error encountered while cancelling the duty"
            }))
        }
    }

    const header = useRef();

    useEffect(()=>{
        if(header?.current){
            header?.current?.click();
        }
    },[header]);


    return(
        
        <Modal    
            visible={visible}
            width={600}
            footer={
                <div 
                    className="drawer-footer"
                >

                    <button disabled={loading || btnDisabled} className={loading || btnDisabled?'disabled-btn drawer-filled-btn':'drawer-filled-btn'} onClick={handelSubmit}>
                       {loading? <Spinner/> : "Confirm"}
                    </button>

                    <button disabled={false}  className={'drawer-outlined-btn'} onClick={close}>
                        Cancel
                    </button>

                </div>
            }
            closable={false}
        >

            <div className='modal-header-title archive-modal-header' ref={header}>
                <span style={{letterSpacing:'0.2px'}}> {isBulk? `Cancel ${duties.length} ${duties.length===1? "Duty" : "Duties"}` : hasCompleted? "Cancel Completed Duty" : "Cancel Duty"} </span>
                <span  onClick={close}><FontAwesomeIcon className='close' icon={faClose}/></span>
            </div>

            <div className="modal-body" style={loading?{opacity:"0.2"} : {opacity:1}}>

                {notice && <Alert type="info" showIcon message={notice} style={{marginBottom:'16px'}}/>}

                {hasCompleted &&
                    <Alert
                        type="error"
                        showIcon
                        style={{marginBottom:'16px'}}
                        message={isBulk? `${completedCount} of these duties ${completedCount===1? "has" : "have"} already been completed` : "This duty has already been completed"}
                        description={
                            <>
                                The work has already been done and recorded. Cancelling marks it as Cancelled,
                                notifies the officer, and saves your name and reason to the duty's audit log.
                                Its calculated pay &amp; bill is not removed.
                                {invoicedCount>0 &&
                                    <div style={{marginTop:'6px', fontWeight:600}}>
                                        {isBulk? `${invoicedCount} of them ${invoicedCount===1? "has" : "have"}` : "It has"} already been invoiced. Cancelling won't change the invoice that was issued.
                                    </div>
                                }
                            </>
                        }
                    />
                }

                <Form.Item>
                    <label>Reason <span style={{color:'red'}}>*</span></label>
                    <Select
                        placeholder="Reason to cancel"
                        onChange={(e)=>setReason(e)}
                        className="custom-select"
                        value={reason}
                        suffixIcon={<CaretDownFilled/>}
                    >
                        {reasons.map((r)=>(
                            <Option key={r.value} value={r.value}>{r.label}</Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item>
                    {hasCompleted?
                        <label>Why {isBulk? "are these duties" : "is this completed duty"} being cancelled? <span style={{color:'red'}}>*</span></label>
                    :
                        <label>Comments {reason==="other"? <span style={{color:'red'}}>*</span> : "(if any?)"}</label>
                    }
                    <TextArea 
                        placeholder={hasCompleted? "Explain specifically what happened - this is saved to the audit log" : "Comments if any?"}
                        onChange={(e)=>setComment(e.target.value)}
                        value={comment}
                        className="generic-input-control"
                        rows={hasCompleted? 3 : 2}
                    />
                    {hasCompleted && trimmedComment.length<COMPLETED_CANCEL_MIN_COMMENT &&
                        <div style={{fontSize:'12px', color:'#7c98b6', marginTop:'4px'}}>
                            At least {COMPLETED_CANCEL_MIN_COMMENT} characters ({trimmedComment.length}/{COMPLETED_CANCEL_MIN_COMMENT})
                        </div>
                    }
                </Form.Item>

                {hasCompleted &&
                    <Form.Item>
                        <label>Type <b>{CONFIRM_WORD}</b> to cancel {completedCount===1? "a completed duty" : `${completedCount} completed duties`} <span style={{color:'red'}}>*</span></label>
                        <Input
                            placeholder={CONFIRM_WORD}
                            value={typedConfirm}
                            onChange={(e)=>setTypedConfirm(e.target.value)}
                            className="generic-input-control"
                            autoComplete="off"
                            onPaste={(e)=>e.preventDefault()}
                        />
                    </Form.Item>
                }

                
            </div>

                <BannedEmployeeFromSite
                    visible={bannedEmployeeVisible}
                    close={()=>{setBannedEmployeeVisible(false); close();}}
                    cancelledDutyData={cancelledDutyData || {}}
                    refetch={refetch}
                />
        </Modal>
    )
}


export const BannedEmployeeFromSite = ({close, visible, cancelledDutyData, refetch})=>{

    const dispatch = useDispatch();

    const [banEmployee, {banEmployeeLoading}] = useMutation(newBannedSiteMutation);

    const [deleteFutureDuties, {loading: deleteFutureDutiesLoading}] = useMutation(deleteBannedEmployeeFutureDutiesMutation);
    
    const {assignedEmployee:assignedEmployeeId, firstDate, siteId} = cancelledDutyData;

    const handelBanEmployee = async()=>{
        try{   

            await banEmployee({
                variables:{
                    input:[{
                            employeeId: assignedEmployeeId,
                            siteId: siteId,
                    }]
                }
            });

            await deleteFutureDuties({
                variables:{
                    assignedEmployee: assignedEmployeeId,
                    date: firstDate
                }
            })

            close();

            dispatch(setNotification({
                error: false,
                notificationState: true,
                message: "Employee was banned for this site"
            }));

            await refetch();

        }catch(err){

            dispatch(setNotification({
                error: true,
                message: "An error encountered while banning this employee",
                notificationState: true,
            }));
        }
    };

    return(
        
        <Modal    
            visible={visible}
            width={600}
            footer={
                <div 
                    className="drawer-footer"
                >

                    <button disabled={banEmployeeLoading || deleteFutureDutiesLoading} className={banEmployeeLoading || deleteFutureDutiesLoading?'disabled-btn drawer-filled-btn':'drawer-filled-btn'} onClick={handelBanEmployee}>
                       {banEmployeeLoading || deleteFutureDutiesLoading? <Spinner/> : "Confirm"}
                    </button>

                    <button disabled={banEmployeeLoading || deleteFutureDutiesLoading}  className={'drawer-outlined-btn'} onClick={close}>
                        Cancel
                    </button>

                </div>
            }
            closable={false}
        >

            
            <div className='modal-header-title archive-modal-header'>
                <span style={{letterSpacing:'0.2px'}}> Cancel Duty </span>
                <span  onClick={close}><FontAwesomeIcon className='close' icon={faClose}/></span>
            </div>

            <div className="modal-body" style={{color:'black'}}>
                Do you want to <b style={{color:'red'}}> Ban this employee </b> on this site?
                <div style={{color:'red', fontWeight:'bold', fontSize:'15px'}}>The officer will be removed from all future scheduled duties.</div>
            </div>

        </Modal>

    )
};


// All the future scheduled duties for this officer will be deleted

